import React, { useState, useRef, useEffect, MouseEvent } from 'react'
import { isEqual } from 'lodash-es'
import {
  useFloating,
  flip,
  shift,
  useInteractions,
  useClick,
  useDismiss,
  FloatingArrow,
  arrow,
  FloatingPortal,
} from '@floating-ui/react'
import DateDisplay from './DateDisplay'
import EnlargedTimelineEditor from './EnlargedTimelineEditor'
import { InternalTimelineEditorValue } from '../TimelineEditor'
import { getFloatingPortalProps } from '@utils/portal_target'

type CompactTimelineEditorProps = {
  value: InternalTimelineEditorValue
  onChange?: (value: InternalTimelineEditorValue) => void
}

const CompactTimelineEditor: React.FC<CompactTimelineEditorProps> = ({ value, onChange }) => {
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [selectedValue, setSelectedValue] = useState<InternalTimelineEditorValue>(value)

  const arrowRef = useRef(null)

  const handleChange = (obj: InternalTimelineEditorValue) => {
    const nextSelectedValue = {
      ...selectedValue,
      ...obj,
    }
    setSelectedValue(nextSelectedValue)
  }

  const handleReferenceClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    togglePopover()
  }

  const triggerChange = () => {
    if (onChange && !isEqual(selectedValue, value)) {
      onChange(selectedValue)
    }
  }

  const togglePopover = () => {
    const nextPopoverVisibleValue = !popoverVisible
    if (!nextPopoverVisibleValue) {
      triggerChange()
    }
    setPopoverVisible(nextPopoverVisibleValue)
  }

  const { refs, floatingStyles, context } = useFloating({
    open: popoverVisible,
    onOpenChange: togglePopover,
    middleware: [flip(), shift({ crossAxis: true, padding: 16 }), arrow({ element: arrowRef })],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, {
      outsidePress: (event: any): boolean => !event.target.closest('#js-calendar-target'),
    }),
  ])

  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  const reference = (
    <button
      ref={refs.setReference}
      {...getReferenceProps()}
      onClick={handleReferenceClick}
      className="btn btn--outline"
    >
      <DateDisplay value={value} />
    </button>
  )

  const popover = popoverVisible && (
    <FloatingPortal {...getFloatingPortalProps()}>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        className="popover popover--opaque popover--above-modal p"
      >
        <FloatingArrow ref={arrowRef} context={context} className="floating-arrow" />
        <EnlargedTimelineEditor value={selectedValue} onChange={handleChange} />
      </div>
    </FloatingPortal>
  )

  return (
    <>
      {reference}
      {popover}
    </>
  )
}

export default CompactTimelineEditor
